import React from "react";
import {
  AppBar, Toolbar, Typography, Container, Box, Grid, Card, CardContent, CardActions,
  IconButton, Button, Badge, Drawer, Divider, List, ListItem, ListItemText,
  ListItemAvatar, Avatar, Tabs, Tab, TextField, InputAdornment, Snackbar, Alert, Chip,
  CircularProgress, Stack, Skeleton, CssBaseline, GlobalStyles, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Autocomplete
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { ShoppingCart, Add, Remove, Search, Send, Tune, ArrowBack, Close, MenuBookOutlined } from "@mui/icons-material";
import { explainBackendError, validateOrderPayload, cents, fromc } from "./components/validationHelpers";
import PaymentSheet from "./components/PaymentSheet";
import ComboPickerSheet from "./components/ComboPickerSheet";
import OrderSummary from "./components/OrderSummary";
import dayjs from 'dayjs';

import Client from '../Client';
import Config from '../ClientConfig';

const ENDPOINTS = {
  location: (loc) => `${Config.BASE_API_URL}location/${encodeURIComponent(loc)}`,
  priceList: (loc) => `${Config.BASE_API_URL}priceList/clientList/?location=${encodeURIComponent(loc)}`,
  promotionList: (loc) => `${Config.BASE_API_URL}promotionList/clientList/?location=${encodeURIComponent(loc)}`,
  products: () => `${Config.BASE_API_URL}product/list_products/`,
  combos: () => `${Config.BASE_API_URL}combo/list_combos/`,
  categories: () => `${Config.BASE_API_URL}productCategory/`,
  subcategories: () => `${Config.BASE_API_URL}productSubCategory/`,
  createOrder: () => `${Config.BASE_API_URL}orders/add/`,
  registerPay: () => `${Config.BASE_API_URL}orders/registerPay/`,
  orderPayStatus: (id) => `${Config.BASE_API_URL}orders/getPayStatus/?iref=${encodeURIComponent(id)}`,
  cancelOrder: (id) => `${Config.BASE_API_URL}orders/cancelOrder/?iref=${encodeURIComponent(id)}`,
};
const API_URL_LOCATIONS = Config.API_URL_LOCATIONS + 'list_locations/';
const RESTAURANT_NAME = "CARTOFISSERIE";

function money(n, currency = "RON") {
  if (typeof n !== "number" || Number.isNaN(n)) {return "-";}
  return new Intl.NumberFormat("ro-RO", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(n);
}
function ensureSmallImage(path) {
  if (!path) return null;
  const base = Config.BASE_URL; // <- use current server
  const abs = path.startsWith("http") ? path : `${base}${path}`;
  const i = abs.lastIndexOf(".");
  return i === -1 ? abs : `${abs.slice(0, i)}_s${abs.slice(i)}`;
}
function arrify(raw, keys = []) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw;
  }
  for (const key of keys) {
    if (Array.isArray(raw[key])) {
      return raw[key];
    }
  }
  if (Array.isArray(raw.results)) return raw.results;
  if (Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw.items)) return raw.items;
  return [];
}
function toCents(x){ return Math.round(Number(x)*100); }
function fromCents(c){ return Math.round(c)/100; }

function buildEffectiveMaps(pl, promoRaw) {
  const promoObj = promoRaw?.promotionList || promoRaw || {};
  const promoByProductId = {};
  (arrify(promoObj, ["productPromotion_list"]) || []).forEach(l => {
    if (l?.isActive !== false && l?.product && typeof l?.value === "number") {
      promoByProductId[l.product] = Number(l.value);
    }
  });
  const promoByComboId = {};
  (arrify(promoObj, ["comboPromotion_list"]) || []).forEach(l => {
    if (l?.isActive !== false && l?.productCombo && typeof l?.value === "number") {
      promoByComboId[l.productCombo] = Number(l.value);
    }
  });

  const effectiveProd = { ...(pl?.priceByProductId || {}) };
  Object.keys(promoByProductId).forEach(pid => { effectiveProd[pid] = promoByProductId[pid]; });

  const effectiveCombo = { ...(pl?.priceByComboId || {}) };
  Object.keys(promoByComboId).forEach(cid => { effectiveCombo[cid] = promoByComboId[cid]; });

  return { promoByProductId, promoByComboId, effectiveProd, effectiveCombo };
}

function parseHMS(hms) { // "HH:MM:SS" -> minutes from midnight
  if (!hms) return null;
  const [H, M, S] = hms.split(":").map(Number);
  return (H * 60) + (M || 0) + Math.floor((S || 0) / 60);
}

function fmtHM(mins) { // 780 -> "13:00"
  if (mins == null) return "--:--";
  let h = Math.floor(mins / 60);
  let m = mins % 60;
  return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`;
}

// Map JS getDay() (0..6, Sunday=0) to API day (1..7, Monday..Sunday)
function apiWeekdayFromDate(d) {
  const js = d.getDay(); // 0..6 (Sun..Sat)
  return js === 0 ? 7 : js; // 1..7 (Mon..Sun)
}

function parseOrderApi(resOrErr) {
  let r = (resOrErr && resOrErr.response) ? resOrErr.response : resOrErr;

  // If r already looks like your API payload (has status/code/module), use it.
  let body = (r && typeof r === "object" && (("status" in r) || ("code" in r) || ("module" in r)))
    ? r
    : (r && r.data !== undefined ? r.data : r);

  if (typeof body === "string") {
    try { body = JSON.parse(body); } catch (_) {}
  }
  if (!body || typeof body !== "object") {
    return { ok: false, code: null, message: "Răspuns neașteptat de la server. (empty body)", detail: null, raw: r };
  }

  const status = (body.status || "").toLowerCase();
  if (status === "ok") {
    return {
      ok: true,
      orderId: body?.data?.order ?? null,
      totalAmount: body?.data?.totalAmount ?? null,
      code: body.code ?? null,
      message: body.message ?? null,
      raw: body,
    };
  }
  if (status === "error") {
    const detail = body?.data?.message || body?.data?.detail || body?.data?.error || body?.data?.reason || null;
    const parts = [];
    if (body.code) parts.push(String(body.code));
    if (body.message) parts.push(String(body.message));
    if (detail) parts.push(String(detail));
    return { ok: false, code: body.code ?? null, message: parts.join(" — ") || "There was an error saving the order.", detail, raw: body };
  }

  // Fallback success if status missing but data.order present
  if (body?.data?.order) {
    return { ok: true, orderId: body.data.order, totalAmount: body?.data?.totalAmount ?? null, code: body.code ?? null, message: body.message ?? null, raw: body };
  }
  return { ok: false, code: body.code ?? null, message: "Unexpected response from the server.", detail: null, raw: body };
}


const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#1976d2" },   // Professional blue
    secondary: { main: "#3b4c55ff" }, // Muted gray-blue
    background: { default: "#ffffff", paper: "#fafafa" },
    text: { primary: "#212121", secondary: "#414141ff" },
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: "'Inter', 'Roboto', sans-serif",
    button: { textTransform: "none", fontWeight: 600 },
  },
});

export default class SkipTheLinePage extends React.Component{
  constructor(props){
    super(props);
    this.state = {
      client: new Client('none'),
      location: null,
      locationList: [],
      selectingLocation: false,
      paymentType: 6,
      locationInfo: null,
      locationOpenNow: false,
      locationReason: "",   // explanation if blocked
      loading: true, error: "", 
      priceList:null,
      promoList: null,                        // raw promo list
      promoByProductId: {},                   // productId -> promo price (RON)
      promoByComboId: {},                     // comboId   -> promo price (RON) if present
      effectiveProd: {},                      // productId -> price used (RON)
      effectiveCombo: {},                     // comboId   -> price used (RON)
      priceByProductId: {},   // base price list
      priceByComboId:   {},   // base price list
      productsAll:[], products:[], combos:[],
      categories:[], categoriesRaw:[], subcategories:[],
      view:"name", activeCategory:"All", search:"",
      cartOpen:false, cartProducts:{}, cartCombos:{},
      pickerOpen:false, pickerCombo:null,
      description:"",
      placing:false, orderLocked:false, lastOrderId:null, lastOrderGross:0,
      paymentSheetOpen:false, paymentChecking:false, paymentMessage:"", payUrl:"", payIframeError:false,
      summaryOpen:false,
      scheduledOn: null,          // selected pickup day+time (dayjs)
      scheduleTab: 0,             // 0..6 — which day tab is active (“Today”=0)
      showScheduleModal: false,
      discountMode: "percent",   // "percent" | "value"
      discountInput: "",         // user-entered string
    };
    this.pollTimer = null;
  }

  async componentDidMount() {
    try {
      this.setState({ selectingLocation: true });
      const resp = this.state.client.getData(API_URL_LOCATIONS, { status: 1 }) || {};
      const locationList = Array.isArray(resp.locations) ? resp.locations : [];
      locationList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      this.setState({ locationList, selectingLocation: false }, () => {
        if (this.state.location) this.bootstrap();
      });
    } catch (e) {
      console.warn("Failed to load locations", e);
      this.setState({ selectingLocation: false, error: "Can't load locations list." });
      if (this.state.location) this.bootstrap();
    }
  }
  componentWillUnmount(){ this.stopPolling(); }

  changeLocation() {
    const { cartProducts, cartCombos } = this.state;
    const hasItems =
      Object.keys(cartProducts).length > 0 || Object.keys(cartCombos).length > 0;
    if (hasItems) {
      const confirmed = window.confirm(
        "Changing location will delete cart. Do you want to continue?"
      );
      if (!confirmed) return; // ← Cancel keeps you on the same page
    }

    localStorage.removeItem('selectedLocation');
    this.setState({
      location: null,
      locationInfo: null,
      priceList: null,
      promoList: null,
      cartProducts: {},   // standardize on these, avoid `cart: []`
      cartCombos: {},
      view: 'name',   // or 'name' if that's your first step; pick ONE place to set it
      activeCategory: 'All',
      search: '',
      scheduledOn: null,
      showScheduleModal: false,
      scheduleTab: 0,
      discountMode: "percent",
      discountInput: "",
      placing: false,
      orderLocked: false,
      description: "",
    });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async bootstrap(){
    try{
      const { location } = this.state;
      const [locRaw, plRaw, promoRaw, prodsRaw, combosRaw, catsRaw, subsRaw] = await Promise.all([
        this.state.client.getData(ENDPOINTS.location(location), {}) || null,
        this.state.client.getData(ENDPOINTS.priceList(location), {}) || null,
        this.state.client.getData(ENDPOINTS.promotionList(location), {}) || null,
        this.state.client.getData(ENDPOINTS.products(), {}) || null,
        this.state.client.getData(ENDPOINTS.combos(), {}) || null,
        this.state.client.getData(ENDPOINTS.categories(), {}) || null,
        this.state.client.getData(ENDPOINTS.subcategories(), {}) || null,
      ]);

      const locationInfo = locRaw || null;
      const openEval = this.evalLocationOpen(locationInfo, new Date());
      const pl = this.normalizePriceList(plRaw);
      const promoObj = promoRaw?.promotionList || promoRaw || null;
      const promoList = promoObj;

      const { promoByProductId, promoByComboId, effectiveProd, effectiveCombo } =
        buildEffectiveMaps(pl, promoObj);

      const catArr = arrify(catsRaw,["productCategories"]);
      const subArr = arrify(subsRaw,["productSubCategories"]);
      const catMap = new Map(catArr.map(c=>[c.iref||c.id,c]));

      const productsAll = this.normalizeProducts(
        arrify(prodsRaw,["products","data","items"]), pl, catMap, effectiveProd, promoByProductId
      );
      const products = productsAll.filter(p => {
        const inPromo = promoByProductId[p.id] != null;
        const inPrice = pl.priceByProductId[p.id] != null;
        return p.showStandalone && (inPromo || inPrice);
      });

      // Filter: only combos in promoList or priceList
      const combos = this.normalizeCombos(
        arrify(combosRaw,["productCombos","combos","data"]),
        pl,
        effectiveCombo,
        promoByProductId,
        promoByComboId,
        catMap
      );

      const apiCats = catArr.filter(c=>c.isForSale!==false)
        .map(c=>({ id:c.iref||c.id, name:c.name })).filter(c=>!!c.name);
      const categories = ["All", ...apiCats.map(c=>c.name), "Combos"];

      this.setState({
        priceList: pl,
        promoList,
        promoByProductId, promoByComboId,
        effectiveProd, effectiveCombo,
        productsAll, products, combos, categories,
        categoriesRaw: catArr, subcategories: subArr,
        locationInfo: locationInfo,
        locationOpenNow: openEval.openNow,
        locationReason: openEval.reason,
        loading: false,
      });
    }catch(e){
      console.error(e);
      this.setState({ error:String(e.message||e), loading:false });
    }
  }

  productById = (id) => (this.state.productsAll || []).find(p => p.id === id) || null;

  normalizePriceList(raw){
    const obj = raw?.priceList || raw || {};
    const id = obj?.iref;
    const withVAT = !!obj?.withVAT;
    const currency = obj?.currency===0?"RON":"RON";
    const prodLines = arrify(obj,["productPrice_list","productPrices"])
      .map(l=>({ product: typeof l.product==='object'?(l.product?.iref||l.product?.id):l.product, value:Number(l.value), isActive:l?.isActive!==false }))
      .filter(l=>l.product && Number.isFinite(l.value));
    const comboLines = arrify(obj,["productCombo_list","comboPrices"])
      .map(l=>({ combo: typeof l.productCombo==='object'?(l.productCombo?.iref||l.productCombo?.id):l.productCombo, value:Number(l.value), isActive:l?.isActive!==false }))
      .filter(l=>l.combo && Number.isFinite(l.value));
    const priceByProductId = {}; prodLines.forEach(l=>{ if(l.isActive) priceByProductId[l.product]=l.value; });
    const priceByComboId = {}; comboLines.forEach(l=>{ if(l.isActive) priceByComboId[l.combo]=l.value; });
    return { id, withVAT, currency, priceByProductId, priceByComboId };
  }

  normalizeProducts(arr, pl, catMap, effectiveProd, promoByProductId){
    return (arr||[]).map(p=>{
      const id = p.iref;
      const name = p.name || "";
      const shortName = p.shortName || "";
      const categoryId = p.pCategory || null;
      const subcategoryId = p.pSubCategory || null;
      const vatId = p.vat || null;
      const status = p.status;
      const isForSale = p.isForSale ?? true;
      const isSoldAlone = p.isSoldAlone ?? true;
      const imgMain = (p.productImage_product||[]).find(i=>i.isMain) || (p.productImage_product||[])[0];
      const imageSmallUrl = ensureSmallImage(imgMain?.image);
      const categoryName = catMap.get(categoryId)?.name || null;

      const basePrice = pl.priceByProductId[id];
      const effPrice  = effectiveProd ? (typeof effectiveProd === 'function' ? effectiveProd(id) : effectiveProd[id]) : basePrice;
      const isPromo   = promoByProductId ? (promoByProductId[id] != null) : false;

      const hasPrice = (effPrice != null); // effective price drives sellability
      const sellable = status===2 && isForSale===true;
      const showStandalone = sellable && hasPrice && isSoldAlone!==false;

      return {
        id,name,shortName,categoryId,subcategoryId,categoryName,vatId,status,isForSale,isSoldAlone,
        sellable,hasPrice,showStandalone,imageSmallUrl,
        basePrice, effPrice, isPromo
      };
    });
  }

  normalizeCombos(arr, pl, effectiveCombo, promoByProductId, promoByComboId = {}, catMap = new Map()) {
    const asKey = v => String(v);
    const hasKey = (obj, k) => Object.prototype.hasOwnProperty.call(obj || {}, asKey(k));
    const getMaybeMap = (m, k) => (m?.get ? m.get(asKey(k)) : m?.[asKey(k)]);

    const priceByComboId   = pl?.priceByComboId   || {};
    const priceByProductId = pl?.priceByProductId || {};

    return (arr || []).map(c => {
      const id = asKey(c.iref ?? c.id);
      const name = c.name || "";
      const shortName = c.shortName || "";
      const categoryId = c.pCategory || null;
      const subcategoryId = c.pSubCategory || null;
      const categoryName = catMap.get(categoryId)?.name || null;

      const basePricePL = priceByComboId[id];
      const ecValue = effectiveCombo ? getMaybeMap(effectiveCombo, id) : undefined;
      const basePriceEff = (ecValue ?? basePricePL);

      const basePriceFromPL = basePricePL;
      const isComboPromo =
        (typeof basePriceEff === "number") &&
        (basePriceFromPL == null || basePriceEff !== basePriceFromPL);

      const imageSmallUrl = ensureSmallImage((c.comboImage_productCombo || [])[0]?.image);

      const cats = (c.comboCategory_productCombo || []).map(k => ({
        id: asKey(k.iref ?? k.id),
        name: k.name,
        categoryId: k.productCategory?.iref || k.productCategory || null,
        subcategoryId: k.productSubCategory?.iref || k.productSubCategory || null,
        priceType: k.priceType ?? 0,
        priceValueC: toCents(k.priceValue || 0),
      }));

      const fixed = (c.combo_productComboProduct || []).map(p => ({
        id: asKey(p.iref ?? p.id),
        name: p.name,
        productId: asKey(String(p.product?.iref ?? p.product)),
        priceType: p.priceType ?? 0,
        priceValueC: toCents(p.priceValue || 0),
      }));

      const sellable = (c.status === 2) && ((c.isForSale ?? true) === true);

      const inPL = hasKey(priceByComboId, id);
      const inPromo = hasKey(promoByComboId, id);

      const allFixedPriced = fixed.every(fi =>
        (fi.priceType === 0 || fi.priceType === 1)
          ? hasKey(priceByProductId, fi.productId) || hasKey(promoByProductId, fi.productId)
          : true
      );

      const show = sellable && (inPL || inPromo) && allFixedPriced;

      if (!show) return null;

      const priceToUse = (ecValue ?? basePricePL);

      return {
        id,
        name,
        shortName,
        categoryId,
        subcategoryId,
        categoryName,
        basePriceC: toCents(priceToUse ?? 0),
        basePricePL: basePriceFromPL ?? null,
        isComboPromo,
        imageSmallUrl,
        categories: cats,
        fixedItems: fixed,
      };
    }).filter(Boolean);
  }

  // Parse a localized number from text input (RON)
  parseNumber = (txt) => {
    const s = String(txt || "").replace(/[^\d.,-]/g, "").replace(",", ".");
    const v = Number(s);
    return Number.isFinite(v) ? v : 0;
  };

  // Returns discount (gross) in cents, capped to subtotal and never below 0.
  computeDiscountGrossC(totalGrossC) {
    const mode = this.state.discountMode;
    const inpRON = this.parseNumber(this.state.discountInput);
    const inpC = Math.max(0, Math.round(inpRON * 100));

    if (totalGrossC <= 0) return 0;

    let wantC = 0;
    if (mode === "percent") {
      const p = Math.max(0, Math.min(100, inpRON));
      wantC = Math.round(totalGrossC * (p / 100));
    } else {
      wantC = inpC;
    }
    return Math.max(0, Math.min(wantC, totalGrossC));
  }

  qtyForProduct = (id)=> (this.state.cartProducts[id]?.qty || 0);
  goHome = () => {
    if (this.state.orderLocked) return;
    if (!this.ensureNameOrWarn()) return;   // 👈 same guard
    this.setState({ view:"home", activeCategory:"All", search:"" });
  };
  openCategory = (name) => {
    if (this.state.orderLocked) return;
    if (!this.ensureNameOrWarn()) return;   // 👈 block navigation if description missing
    this.setState({ activeCategory: name, view: "browse" });
  };

  ensureNameOrWarn = () => {
    const n = (this.state.description || "").trim();
    if (n.length < 2) { this.setState({ view: "name", error: "Add description to continue." }); return false; }
    return true;
  };

  // Build adjusted [start,end) minutes for a given *native* Date
  getAdjustedIntervalsForDate = (date /* Date */, info = this.state.locationInfo) => {
    const dayMins = 24 * 60;
    const clamp = (x, a, b) => Math.max(a, Math.min(b, x));
    const day = apiWeekdayFromDate(date);

    // IMPORTANT: read from the right state key
    const schedules = Array.isArray(info?.locationSchedule_location)
      ? info.locationSchedule_location
      : [];

    const todays = schedules.filter(x => Number(x.day) === Number(day));

    // DELTAS: start + openDelta; end + closeDelta
    const openDelta  = Number(info?.orderOpenDeltaMin || 0);
    const closeDelta = Number(info?.orderCloseDeltaMin || 0);

    const out = [];
    for (const it of todays) {
      const s = parseHMS(it.interval_start); // minutes 0..1439
      const e = parseHMS(it.interval_end);   // minutes 0..1439
      if (s == null || e == null) continue;

      let adjStart = clamp(s + openDelta, 0, dayMins);
      let adjEnd   = clamp(e + closeDelta, 0, dayMins);

      // If an overnight interval exists (end <= start), for *today* keep [start..24:00).
      if (e <= s) {
        adjStart = clamp(s + openDelta, 0, dayMins);
        adjEnd   = dayMins;
      }

      // Prevent delta inversion from dropping short windows (e.g., 23:00–23:30 -> 23:30–23:00)
      adjEnd = Math.max(adjEnd, adjStart);

      if (adjEnd >= adjStart) out.push([adjStart, adjEnd]);
    }

    // sort + merge
    out.sort((a,b) => a[0]-b[0]);
    const merged = [];
    for (const [s,e] of out) {
      if (!merged.length || s >= merged[merged.length - 1][1]) merged.push([s,e]);
      else merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], e);
    }
    return merged;
  };

  allDayChipsForDate = (date) => {
    const intervals = this.getAdjustedIntervalsForDate(date.toDate()); // [start,end) mins
    const isIn = (m, inclusiveEnd = false) =>
      intervals.some(([s, e]) => inclusiveEnd ? (m >= s && m <= e) : (m >= s && m < e));

    const now = dayjs();
    const isToday = date.isSame(now, "day");

    const chips = [];
    // generate 00:00, 00:30, 01:00, 01:30, ... 23:30 (and include 24:00 check below)
    const step = 30;
    const align = 0; // :00/:30
    for (let m = align; m < 24 * 60; m += step) {
      const dt = date.startOf("day").add(m, "minute");
      // allow selecting exactly at the interval end (<=)
      const available = isIn(m, /*inclusiveEnd*/ true) && (!isToday || dt.isAfter(now.add(1, "minute")));
      chips.push({ dt, m, label: dt.format("HH:mm"), available });
    }
    return chips;
  };

  // build 30-min slots aligned to :15/:45 inside adjusted intervals
  buildSlotsForDate = (date) => {
    const intervals = this.getAdjustedIntervalsForDate(date.toDate());
    if (!intervals.length) return [];

    const now = dayjs();
    const isToday = date.isSame(now, "day");

    const step = 30;    // every 30 mins
    const align = 0;    // :00 and :30

    const out = [];
    intervals.forEach(([start,end]) => {
      // first time >= start aligned to :15/:45
      let m = start;
      const mod = ((m - align) % step + step) % step;
      if (mod !== 0) m += (step - mod);

      for (; m <= end; m += step) {
        const slot = date.startOf("day").add(m, "minute");
        if (isToday && slot.isBefore(now.add(1, "minute"))) continue;
        out.push(slot);
      }
    });

    // de-dup by timestamp
    const seen = new Set();
    return out.filter(s => {
      const k = s.valueOf();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  };

  isScheduledOk = () => {
    const s = this.state.scheduledOn;
    if (!s) return false;
    const limit = dayjs().endOf("day").add(7, "day"); // max 7 days from today
    if (s.isAfter(limit)) return false;
    const daySlots = this.buildSlotsForDate(s.startOf("day"));
    return daySlots.some(x => x.isSame(s));
  };

  evalLocationOpen = (info, now = new Date()) => {
    if (!info) return { openNow: false, reason: "No details for the location." };

    const allowedServe = new Set([2,4,6,9]);
    if (info.status !== 1) return { openNow: false, reason: "Location is not active." };
    if (!allowedServe.has(info.serveType)) return { openNow: false, reason: "Location is not accepting orders." };

    const nowMins = now.getHours() * 60 + now.getMinutes();

    // IMPORTANT: reuse the same adjusted-interval logic
    const iv = this.getAdjustedIntervalsForDate(
      new Date(now.getFullYear(), now.getMonth(), now.getDate()),
      info
    );

    if (!iv.length) return { openNow: false, reason: "There is no schedule for today." };

    const openNow = iv.some(([s,e]) => nowMins >= s && nowMins < e);
    return { openNow, reason: null };
  };

  // Find the first future start strictly after "now"
  getNextAvailableSlotUsingIntervals = (nowDayjs = dayjs()) => {
    const maxDays = 7;
    const now = nowDayjs;
    const todayDate = now.startOf("day").toDate();
    const nowMins = now.hour() * 60 + now.minute();

    // Today: first start after now
    const todayIv = this.getAdjustedIntervalsForDate(todayDate);
    for (const [s, e] of todayIv) {
      if (nowMins < e && s > nowMins) {
        return { when: now.startOf("day").add(s, "minute"), dayOffset: 0 };
      }
    }

    // Next days: first interval's start
    for (let i = 1; i <= maxDays; i++) {
      const d = now.add(i, "day");
      const iv = this.getAdjustedIntervalsForDate(d.startOf("day").toDate());
      if (iv.length) {
        const [s] = iv[0];
        return { when: d.startOf("day").add(s, "minute"), dayOffset: i };
      }
    }
    return null;
  };

  // Formats today's schedule as "HH:mm–HH:mm". Tries intervals if you have them; falls back to slots.
  formatScheduleForDay = (date) => {
    // If you have a function or state that returns interval minutes like [[startMin, endMin], ...],
    // plug it in here. Example:
    const intervals = this.intervalsForDate?.(date); // optional; remove if you don't have it
    if (Array.isArray(intervals) && intervals.length) {
      return intervals
        .map(([s, e]) => {
          const base = date.startOf("day");
          return `${base.add(s, "minute").format("HH:mm")}–${base.add(e, "minute").format("HH:mm")}`;
        })
        .join(", ");
    }

    // Fallback: infer from built slots (first and last)
    const slots = this.buildSlotsForDate(date);
    if (!slots?.length) return "—";
    const start = slots[0].format("HH:mm");
    const end = slots[slots.length - 1].format("HH:mm");
    return `${start}–${end}`;
  };

  renderScheduleStatusLine = () => {
    const now = dayjs();
    const open = this.state.locationOpenNow === true;

    const iv = this.getAdjustedIntervalsForDate(now.startOf("day").toDate());
    const spanText = iv.length
      ? `${fmtHM(iv[0][0])}–${fmtHM(iv[iv.length - 1][1])}`
      : "–";

    if (open) {
      return `Open now. Schedule today: ${spanText}.`;
    }

    const next = this.getNextAvailableSlotUsingIntervals(now);
    if (!next) {
      return this.state.locationReason || `Closed now. No available schedule in the next 7 days.`;
    }

    const label =
      next.dayOffset === 0
        ? `today at ${next.when.format("HH:mm")}`
        : next.dayOffset === 1
        ? `tomorrow at ${next.when.format("HH:mm")}`
        : `${next.when.format("dddd")}, ${next.when.format("HH:mm")}`;

    return (
      this.state.locationReason ||
      `Closed now. Next available schedule: ${label}.`
    );
  };

  // ——— price maps shortcuts
  getPriceContext = () => ({
    plByProd: this.state.priceList?.priceByProductId || {},
    priceListId: this.state.priceList?.id || null,
    promoByProd: this.state.promoByProductId || {},
    promoListId: this.state.promoList?.iref || null,
    // Optional: preload of other pricelists if you have them
    priceByProductByList: this.state.priceByProductByList || {}, // { [plId]: { [productId]: priceRON } }
  });

  getPriceFromList = (productId, listId) => {
    if (!listId) return null;
    const { priceByProductByList } = this.getPriceContext();
    const m = priceByProductByList[listId];
    return (m && m[productId] != null) ? Number(m[productId]) : null;
  };

  // Decide where a product’s price comes from given the combo slot type (0|1|2)
  priceSourceForProduct = (productId, slotType) => {
    const { plByProd, priceListId, promoByProd, promoListId } = this.getPriceContext();
    const hasPL    = (plByProd[productId] != null) || (this.getPriceFromList(productId, priceListId) != null);
    const hasPromo = !!promoListId && (promoByProd[productId] != null);

    if (slotType === 1) return hasPromo ? "promo" : (hasPL ? "pl" : null);
    if (slotType === 0) return hasPL ? "pl" : (hasPromo ? "promo" : null);   // anchor: PL first, else promo
    return hasPL ? "pl" : (hasPromo ? "promo" : null);                        // type‑2: prefer PL
  };

  // Which list id to annotate on a component (PL id or promo iref)
  priceListIdForProduct = (productId, slotType) => {
    const { priceListId, promoListId } = this.getPriceContext();
    const src = this.priceSourceForProduct(productId, slotType);
    return src === "promo" ? (promoListId || null) : (priceListId || null);
  };

  // *** The anchor used for type‑0 allocation (per product) ***
  anchorForType0 = (productId) => {
    const { plByProd, promoByProd } = this.getPriceContext();
    const src = this.priceSourceForProduct(productId, 0);

    if (src === "promo") {
      return Number(promoByProd[productId] ?? 0);
    }

    // src === "pl": try that product’s own PL id (if different), else current PL
    const compPLId = this.priceListIdForProduct(productId, 0);
    const vFromCompPL = this.getPriceFromList(productId, compPLId);
    if (vFromCompPL != null) return Number(vFromCompPL);

    const vCur = plByProd[productId];
    if (vCur != null) return Number(vCur);

    // final fallback
    return Number(promoByProd[productId] ?? 0);
  };

  addProduct = (p) => {
    if (this.state.orderLocked) return;
    if (!this.ensureNameOrWarn()) return;
    const effUnit = (this.state.promoByProductId?.[p.id] ?? this.state.priceList?.priceByProductId?.[p.id] ?? 0);
    const unitC = toCents(effUnit);
    const priceSource =
      (this.state.promoByProductId?.[p.id] != null) ? 'promo'
      : (this.state.priceList?.priceByProductId?.[p.id] != null) ? 'price'
      : null;

    this.setState(({ cartProducts }) => ({
      cartProducts: {
        ...cartProducts,
        [p.id]: {
          id: p.id,
          name: p.name,
          imageSmallUrl: p.imageSmallUrl,
          unitPriceC: unitC,
          qty: (cartProducts[p.id]?.qty || 0) + 1,
          vatId: p.vatId,
          priceSource,
        },
      },
    }));
  };

  incProduct = (id) => {
    if (this.state.orderLocked) return null;
    this.setState(({ cartProducts }) => ({
      cartProducts: {
        ...cartProducts,
        [id]: {
          ...cartProducts[id],
          qty: (cartProducts[id]?.qty || 0) + 1,
        },
      },
    }));
  };
  decProduct = (id)=>{
    if (this.state.orderLocked) return;
    this.setState(({cartProducts})=>{ 
      const line=cartProducts[id]; 
      if(!line) return {cartProducts}; 
      const next={...cartProducts}; 
      if(line.qty<=1) delete next[id]; 
      else next[id]={...line, qty: line.qty-1}; 
      return {cartProducts: next}; 
    });
  };
  updateProductQty = (id, newQty) => {
    this.setState((prev) => {
      const cartProducts = { ...prev.cartProducts };
      if (cartProducts[id]) {
        cartProducts[id] = { ...cartProducts[id], qty: newQty };
      }
      return { cartProducts };
    });
  };

  openComboPicker = (combo) => {
    if (this.state.orderLocked) return;
    if (!this.ensureNameOrWarn()) return;
    this.setState({
      pickerOpen: true,
      pickerCombo: combo,
    });
  };
  closeComboPicker = ()=> this.setState({ pickerOpen:false, pickerCombo:null });

  confirmComboFromPicker = (selections, selectionDetails, meta) => {
    const { pickerCombo } = this.state;

    // attach fixed items + vatId from product catalog
    const fixedItems = (pickerCombo.fixedItems || []).map(fi => {
      const prod = this.productById(fi.productId);
      return {
        name: fi.name,
        productId: fi.productId,
        priceType: fi.priceType,        // 0/1/2
        priceValueC: fi.priceValueC,    // cents
        vatId: prod?.vatId || prod?.vat || null,
      };
    });

    const key = pickerCombo.id + '|' + Object.values(selections).map(x => (x.id || x)).sort().join(',');

    this.setState(({ cartCombos }) => ({
      cartCombos: {
        ...cartCombos,
        [key]: {
          id: pickerCombo.id,
          name: pickerCombo.name,
          imageSmallUrl: pickerCombo.imageSmallUrl,
          qty: (cartCombos[key]?.qty || 0) + 1,
          basePriceC: pickerCombo.basePriceC,
          selections,
          selectionDetails,
          fixedItems,
          priceSource: meta?.isComboPromo ? 'promo' : 'price', // 👈
        },
      },
      pickerOpen: false,
      pickerCombo: null,
    }));
  };

  incCombo = (key) => {
    if (this.state.orderLocked) return null;
    this.setState(({ cartCombos }) => ({
      cartCombos: {
        ...cartCombos,
        [key]: {
          ...cartCombos[key],
          qty: cartCombos[key].qty + 1,
        },
      },
    }));
  };
  decCombo = (key)=>{
    if (this.state.orderLocked) return;
    this.setState(({cartCombos})=>{ 
      const line=cartCombos[key]; 
      if(!line) return {cartCombos}; 
      const next={...cartCombos}; 
      if(line.qty<=1) delete next[key]; 
      else next[key]={...line, qty: line.qty-1}; 
      return {cartCombos: next}; 
    });
  };
  updateComboQty = (key, newQty) => {
    this.setState((prev) => {
      const cartCombos = { ...prev.cartCombos };
      if (cartCombos[key]) {
        cartCombos[key] = { ...cartCombos[key], qty: newQty };
      }
      return { cartCombos };
    });
  };

  clearCart = ()=> this.state.orderLocked ? null : this.setState({ cartProducts:{}, cartCombos:{} });
  clearSchedule = () => this.setState({ scheduledOn: null });

  vatRate = (vatId)=> ({"16ef0e4256":21,"27554c6052":0,"ad0aa21a47":11}[vatId] ?? 0);

  computeTotals() {
    const { cartProducts, cartCombos } = this.state;
    let prodGrossC = 0, prodVatC = 0;
    let comboGrossC = 0, comboVatC = 0;

    // ----- products -----
    Object.values(cartProducts).forEach(l => {
      const unit = l.unitPriceC;                // cents
      const qty  = l.qty;
      const rate = this.vatRate(l.vatId);       // %
      const lineC = unit * qty;
      const vatC  = Math.round(lineC * rate / (100 + rate));
      prodGrossC += lineC;
      prodVatC   += vatC;
    });

    // ----- combos -----
    Object.values(cartCombos).forEach(line => {
      const { basePriceC, qty, selectionDetails = [], fixedItems = [] } = line;

      const allItems = [
        ...selectionDetails,
        ...fixedItems.map(fi => ({
          productId: fi.productId,
          priceType: fi.priceType,
          priceValueC: fi.priceValueC,
          vatId: fi.vatId,
          name: fi.name,
        })),
      ];

      const type1 = allItems.filter(s => s.priceType === 1);
      const type2 = allItems.filter(s => s.priceType === 2);
      const type0 = allItems.filter(s => s.priceType === 0);

      const effProdMap = this.state.effectiveProd || {};
      const v1 = type1.map(s => s.productId
        ? Math.round(((typeof effProdMap === "function") ? effProdMap(s.productId) : effProdMap[s.productId] || 0) * 100)
        : 0
      );
      const sum1 = v1.reduce((a, b) => a + b, 0);

      const v2 = type2.map(s => s.priceValueC || 0);
      const sum2 = v2.reduce((a, b) => a + b, 0);

      const unitLineC = (basePriceC || 0) + sum1 + sum2;

      // allocate VAT for each component (same as your previous logic)
      let unitVatC = 0;

      // type-0 allocation by anchors
      const P0 = type0.map(s => this.anchorForType0(s.productId));
      const S0 = P0.reduce((a, b) => a + b, 0);
      const baseUnitRON = Math.round(((basePriceC || 0) / 100) * 100) / 100;

      let alloc0UnitRON = [];
      if (type0.length) {
        if (S0 <= 0) {
          const each = Math.round((baseUnitRON / type0.length) * 100) / 100;
          alloc0UnitRON = Array(type0.length).fill(each);
          const drift = Math.round((baseUnitRON - alloc0UnitRON.reduce((a,b)=>Math.round((a+b)*100)/100,0)) * 100)/100;
          if (Math.abs(drift) >= 0.01) alloc0UnitRON[0] = Math.round((alloc0UnitRON[0] + drift) * 100)/100;
        } else {
          alloc0UnitRON = P0.map(p => Math.round((baseUnitRON * (p / S0)) * 100) / 100);
          let sum = Math.round(alloc0UnitRON.reduce((a,b)=>Math.round((a+b)*100)/100,0) * 100)/100;
          let rem = Math.round((baseUnitRON - sum) * 100) / 100;
          if (Math.abs(rem) >= 0.01) {
            const idx = P0.indexOf(Math.max(...P0));
            alloc0UnitRON[idx] = Math.round((alloc0UnitRON[idx] + rem) * 100) / 100;
          }
        }
      }

      type0.forEach((s, i) => {
        const rate = this.vatRate(s.vatId);
        unitVatC += Math.round(Math.round(alloc0UnitRON[i] * 100) * rate / (100 + rate));
      });
      type1.forEach((s, i) => {
        const rate = this.vatRate(s.vatId);
        unitVatC += Math.round(v1[i] * rate / (100 + rate));
      });
      type2.forEach((s, i) => {
        const rate = this.vatRate(s.vatId);
        unitVatC += Math.round(v2[i] * rate / (100 + rate));
      });

      comboGrossC += unitLineC * qty;
      comboVatC   += unitVatC * qty;
    });

    const totalGrossCBeforeDisc = prodGrossC + comboGrossC;
    const totalVatCBeforeDisc   = prodVatC + comboVatC;
    const totalNetCBeforeDisc   = totalGrossCBeforeDisc - totalVatCBeforeDisc;

    // --- discount over PRODUCTS ONLY (gross) ---
    const discountGrossC = this.computeDiscountGrossC(totalGrossCBeforeDisc);

    // average VAT via ratio (your requirement)
    const netRatio = totalGrossCBeforeDisc > 0
      ? (totalNetCBeforeDisc / totalGrossCBeforeDisc)
      : 1;

    const discountNetC = Math.round(discountGrossC * netRatio);
    const discountVatC = discountGrossC - discountNetC;

    // apply discount to order totals
    const totalGrossC = Math.max(0, totalGrossCBeforeDisc - discountGrossC);
    const totalNetC   = Math.max(0, totalNetCBeforeDisc   - discountNetC);
    const totalVatC   = Math.max(0, totalGrossC - totalNetC);

    return {
      // before discount (if you ever need to show)
      totalGrossBefore: totalGrossCBeforeDisc / 100,
      totalNetBefore:   totalNetCBeforeDisc / 100,
      totalVatBefore:   totalVatCBeforeDisc / 100,

      // discount parts
      discountGross: discountGrossC / 100,
      discountNet:   discountNetC / 100,
      discountVat:   discountVatC / 100,

      // final totals
      totalGross: totalGrossC / 100,
      totalNet:   totalNetC / 100,
      totalVat:   totalVatC / 100,

      // expose product subtotal (gross) for UI hints
      productGross: prodGrossC / 100,
    };
  }

  buildOrderPayload() {
    const { priceList, cartProducts, cartCombos, description, promoList } = this.state;
    /* console.debug("[promo maps]", {
      promoList: this.state.promoList?.iref,
      promoByProductId: this.state.promoByProductId,
      promoByComboId: this.state.promoByComboId
    }); */

    // ---------- PRODUCTS ----------
    const orderProductEntry_order = [];
    const orderProductPromotionEntry_order = [];

    Object.values(cartProducts).forEach((l) => {
      const unitC = l.unitPriceC;                 // cents
      const qty   = l.qty || 1;
      const rate  = this.vatRate(l.vatId);

      const lineC   = unitC * qty;                // cents
      const lineVat = Math.round(lineC * rate / (100 + rate)); // cents

      const common = {
        value: fromCents(lineC),
        quantity: qty,
        vat: fromCents(lineVat),
      };

      const isPromoProduct = !!(promoList?.iref) && (this.state.promoByProductId?.[l.id] != null);

      if (isPromoProduct) {
        orderProductPromotionEntry_order.push({
          ...common,
          ProductPromotion: {
            promotionList: this.state.promoList.iref,
            product: l.id,
            value: fromCents(unitC), // UNIT price (RON)
          },
        });
      } else {
        orderProductEntry_order.push({
          ...common,
          ProductPrice: {
            priceList: priceList.id,
            product: l.id,
            value: fromCents(unitC), // UNIT price (RON)
          },
        });
      }
    });

    // ---------- COMBOS ----------
    const orderComboEntry_order = [];
    const orderComboPromotionEntry_order = [];

    Object.values(cartCombos).forEach((line) => {
      const { basePriceC, qty = 1, selectionDetails = [], id, fixedItems = [] } = line;

      // Collect components (selected + fixed), ensure vatId present
      const allItems = [
        ...selectionDetails,
        ...fixedItems.map((fi) => {
          const prod = this.productById(fi.productId);
          return {
            productId: fi.productId,
            priceType: fi.priceType,       // 0/1/2
            priceValueC: fi.priceValueC,   // cents for type-2
            vatId: prod?.vatId || prod?.vat || null,
            name: fi.name,
          };
        }),
      ];

      const type1 = allItems.filter((s) => s.priceType === 1);
      const type2 = allItems.filter((s) => s.priceType === 2);
      const type0 = allItems.filter((s) => s.priceType === 0);

      // Type‑0 items MUST exist in pricelist
      /* const missingType0 = type0
        .filter((s) => this.state.priceList?.priceByProductId?.[s.productId] == null)
        .map((s) => s.productId);
      if (missingType0.length) {
        throw new Error(`Combo invalid: produse tip‑0 fără preț în pricelist: ${missingType0.join(", ")}`);
      } */

      // Per‑unit Σ(type‑1) from EFFECTIVE prices (promo > list), cents
      const effProdMap = this.state.effectiveProd || {};
      const v1 = type1.map((s) =>
        s.productId ? toCents(((typeof effProdMap === "function") ? effProdMap(s.productId) : effProdMap[s.productId]) || 0) : 0
      );
      const sum1 = v1.reduce((a, b) => a + b, 0);

      // Per‑unit Σ(type‑2) from fixed values, cents
      const v2 = type2.map((s) => s.priceValueC || 0);
      const sum2 = v2.reduce((a, b) => a + b, 0);

      // Per‑unit full line (cents)
      const unitLineC = (basePriceC || 0) + sum1 + sum2;
      const unitLineRON = fromCents(unitLineC);

      // Allocate BASE ONLY (basePriceC) → type‑0 by pricelist proportions (RON per unit)
      const P0 = type0.map(s => this.anchorForType0(s.productId));
      const S0 = P0.reduce((a, b) => a + b, 0);
      const baseUnitRON = fromCents(basePriceC || 0);

      let alloc0UnitRON = [];
      if (type0.length) {
        if (S0 <= 0) {
          const each = Math.round((baseUnitRON / type0.length) * 100) / 100;
          alloc0UnitRON = Array(type0.length).fill(each);
          const drift = Math.round((baseUnitRON - alloc0UnitRON.reduce((a,b)=>Math.round((a+b)*100)/100,0)) * 100)/100;
          if (Math.abs(drift) >= 0.01) alloc0UnitRON[0] = Math.round((alloc0UnitRON[0] + drift) * 100)/100;
        } else {
          alloc0UnitRON = P0.map(p => Math.round((baseUnitRON * (p / S0)) * 100) / 100);
          let sum = Math.round(alloc0UnitRON.reduce((a,b)=>Math.round((a+b)*100)/100,0) * 100)/100;
          let rem = Math.round((baseUnitRON - sum) * 100) / 100;
          if (Math.abs(rem) >= 0.01) {
            const idx = P0.indexOf(Math.max(...P0));
            alloc0UnitRON[idx] = Math.round((alloc0UnitRON[idx] + rem) * 100)/100;
          }
        }
      }

      // Build whole-line component values (RON)
      const components = [];

      // type‑1 components
      type1.forEach((s, i) => {
        const perUnitRON = fromCents(v1[i]);
        const valRON     = Math.round(perUnitRON * qty * 100) / 100;
        const rate       = this.vatRate(s.vatId);
        const vVat       = Math.round((valRON * rate / (100 + rate)) * 100) / 100;
        components.push({
          value: valRON,
          quantity: qty,
          vat: vVat,
          product: s.productId,
          priceType: 1,
          priceList: this.priceListIdForProduct(s.productId, 1),   // ← slotType=1
        });
      });

      // type‑2 components
      type2.forEach((s, i) => {
        const perUnitRON = fromCents(v2[i]);
        const valRON     = Math.round(perUnitRON * qty * 100) / 100;
        const rate       = this.vatRate(s.vatId);
        const vVat       = Math.round((valRON * rate / (100 + rate)) * 100) / 100;
        components.push({
          value: valRON,
          quantity: qty,
          vat: vVat,
          product: s.productId,
          priceType: 2,
          priceList: this.priceListIdForProduct(s.productId, 2),   // ← slotType=2
        });
      });

      // type‑0 components (allocated base)
      type0.forEach((s, i) => {
        const valRON = Math.round(alloc0UnitRON[i] * qty * 100) / 100;
        const rate   = this.vatRate(s.vatId);
        const vVat   = Math.round((valRON * rate / (100 + rate)) * 100) / 100;
        components.push({
          value: valRON,
          quantity: qty,
          vat: vVat,
          product: s.productId,
          priceType: 0,
          priceList: this.priceListIdForProduct(s.productId, 0),   // ← slotType=0
        });
      });

      // Line value & VAT (RON)
      const lineValueRON = Math.round(unitLineRON * qty * 100) / 100;
      const lineVatRON   = Math.round(components.reduce((a, b) => a + Number(b.vat || 0), 0) * 100) / 100;

      const lineCommon = { value: lineValueRON, quantity: qty, vat: lineVatRON };

      // HARD routing by promo map (no reliance on UI flags)
      const usePromoCombo = !!(promoList?.iref) && (this.state.promoByComboId?.[id] != null);

      if (usePromoCombo) {
        orderComboPromotionEntry_order.push({
          ...lineCommon,
          ComboPromotion: {
            promotionList: this.state.promoList.iref,
            productCombo: id,
            value: fromCents(basePriceC), // UNIT base (RON)
          },
          OrderComboPromotionProductsEntry_OrderComboPromotionEntry: components,
        });
      } else {
        orderComboEntry_order.push({
          ...lineCommon,
          ComboPrice: {
            priceList: this.state.priceList.id,
            productCombo: id,
            value: fromCents(basePriceC), // UNIT base (RON)
          },
          OrderComboProductsEntry_OrderComboEntry: components,
        });
      }
    });

    // ---------- TOTALS & PAYLOAD ----------
    const grossC = [
      ...orderProductEntry_order,
      ...orderProductPromotionEntry_order,
      ...orderComboEntry_order,
      ...orderComboPromotionEntry_order,
    ].reduce((a, x) => a + cents(x.value), 0);

    const vatC = [
      ...orderProductEntry_order,
      ...orderProductPromotionEntry_order,
      ...orderComboEntry_order,
      ...orderComboPromotionEntry_order,
    ].reduce((a, x) => a + cents(x.vat), 0);

    const totals = this.computeTotals(); // uses discount
    const payload = {
      type: 1,
      status: 5,
      deliveryType: 0,
      description: `${String(description || "").trim()}`,
      currency: 0,
      location: this.state.location,
      numberOfLines:
        orderProductEntry_order.length +
        orderProductPromotionEntry_order.length +
        orderComboEntry_order.length +
        orderComboPromotionEntry_order.length,
      totalAmount: totals.totalGross,                 // gross after discount
      // totalAmountWithoutVAT: totals.totalNet,         // net after discount
      totalAmountWithoutVAT: fromCents(
        cents(totals.totalGross) - Math.max(0, vatC - cents(totals.discountVat))
      ),
      discountValue: totals.discountGross,
      discountValueWithoutVAT: totals.discountNet,
      transaction_order: [
        { type: this.state.paymentType, status: 1, platform: 9, amount: totals.totalGross },
      ],
      orderProductEntry_order,
      orderComboEntry_order,
      orderProductPromotionEntry_order,
      orderComboPromotionEntry_order,
    };
    if (this.state.scheduledOn) {
      payload.scheduledOn = this.state.scheduledOn.format("YYYY-MM-DD HH:mm:ssZ");
    }

    const v = validateOrderPayload(payload, this.state.effectiveProd || {});
    if (!v.ok) throw new Error(v.errors.join(" · "));

    return payload;
  }

  startPolling = () => { this.stopPolling(); this.pollTimer = setInterval(this.checkOrderStatus, 30000); };
  stopPolling = () => { if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; } };

  checkout = async () => {
    if (this.state.placing) return;

    // 0) quick guard from state (counts quantities, not keys)
    const cartQty =
      Object.values(this.state.cartProducts || {}).reduce((a, l) => a + (l?.qty || 0), 0) +
      Object.values(this.state.cartCombos   || {}).reduce((a, l) => a + (l?.qty || 0), 0);

    if (cartQty === 0) {
      this.setState({ error: "Add products in cart." });
      return;
    }

    // 1) build payload (can throw)
    let payload;
    try {
      payload = this.buildOrderPayload();
    } catch (err) {
      this.setState({ error: explainBackendError(err) || String(err?.message || err) });
      return;
    }

    // 2) payload guard — counts ALL 4 arrays (prod/combo, normal/promo)
    const totalLines =
      (payload.orderProductEntry_order?.length || 0) +
      (payload.orderProductPromotionEntry_order?.length || 0) +
      (payload.orderComboEntry_order?.length || 0) +
      (payload.orderComboPromotionEntry_order?.length || 0);

    if (totalLines === 0) {
      this.setState({ error: "Add products in cart." });
      return;
    }

    // 3) place order
    this.setState({ placing: true, error: "" });
    try {
      const res = await this.state.client.pushJson(ENDPOINTS.createOrder(), payload);
      const parsed = parseOrderApi(res);
      if (!parsed.ok) throw new Error(parsed.message || "Create order failed.");

      const orderId = parsed.orderId;
      if (!orderId) throw new Error("Missing order ID in success response.");

      // 4) SUCCESS → truly close checkout UI
      const totals = this.computeTotals();
      this.setState({
        placing: false,
        orderLocked: true,
        orderResult: res,
        lastOrderId: orderId,
        lastOrderGross: totals.totalGross || 0,

        // close ALL checkout-related surfaces
        cartOpen: false,
        showScheduleModal: false,
        pickerOpen: false,
        paymentSheetOpen: false,
        payUrl: "",
        paymentMessage: "",
        payIframeError: false,

        // switch to the post-checkout “Thank you / Summary” view
        summaryOpen: true,
      });
    } catch (e) {
      const parsed = parseOrderApi(e);
      const msg = parsed.message || (e && e.message) || "There was an error on saving order.";
      this.setState({ placing: false, error: msg });
    }
  };

  checkOrderStatus = async () => {
    const { lastOrderId } = this.state;
    if (!lastOrderId) return;

    try {
      this.setState({ paymentChecking: true });
      const statusRes = this.state.client.getData(ENDPOINTS.orderPayStatus(lastOrderId), {}) || {};

      const stRaw = statusRes?.data?.orderStatus;
      const st = typeof stRaw === "string" ? parseInt(stRaw, 10) : stRaw;

      // 5 = Paid, 6/7/1/2 may also happen later but payStatus endpoint seems to report 5
      if (st === 5) {
        this.stopPolling();
        this.setState({
          paymentChecking: false,
          paymentMessage: statusRes?.message || "The payment was processed successfully..",
        });
        setTimeout(() => {
          this.setState({ paymentSheetOpen: false, summaryOpen: true });
        }, 400);
        return;
      }

      if (st === 0 || st == null) {
        this.setState({
          paymentChecking: false,
          paymentMessage:
            "The payment is not yet complete. After you confirm the payment, press ‘I have paid.’",
        });
        return;
      }

      if (st === 3) {
        this.stopPolling();
        this.setState({
          paymentChecking: false,
          paymentMessage: "The payment was cancelled. You can try again.",
        });
        return;
      }

      this.setState({
        paymentChecking: false,
        paymentMessage: `Payment status: ${String(stRaw)}.`,
      });
    } catch (e) {
      this.setState({
        paymentChecking: false,
        paymentMessage: "Couldn't check payment status.",
      });
    }
  };

  cancelCurrentOrder = async () => {
    const { lastOrderId } = this.state;
    if (!lastOrderId) return;

    try {
      this.stopPolling();
      await this.state.client.pushData(ENDPOINTS.cancelOrder(lastOrderId), {});
      this.setState({
        paymentSheetOpen: false,
        paymentMessage: "",
        paymentChecking: false,
        payUrl: "",
        orderLocked: false,
        lastOrderId: null,
        lastOrderGross: 0,
        cartProducts: {},
        cartCombos: {},
        discountMode: "percent",
        discountInput: "",
        scheduledOn: null,
        showScheduleModal: false,
        view: "name",
        activeCategory: "All",
        search: "",
        error: "",
        description: "",
      });
    } catch (e) {
      this.setState({ error: "We couldn’t cancel the order. Please try again." });
    }
  };

  renderProductCard = (p) => {
    const price = p.effPrice ?? this.state.priceList?.priceByProductId?.[p.id] ?? null;
    const base  = p.basePrice;
    const promo = !!p.isPromo;
    const qty = this.qtyForProduct(p.id);
    const locked = this.state.orderLocked;

    return (
      <Card variant="outlined"
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          transition: "transform 0.2s ease",
          "&:hover": { transform: "translateY(-3px)", boxShadow: "0 4px 12px rgba(0,0,0,0.12)" },
        }}
      >
        <Box sx={{ position:'relative', height:{ xs:100, sm:120 }, bgcolor:'background.default' }}>
          {p.imageSmallUrl && (
            <Box component="img" src={p.imageSmallUrl} alt={p.name}
                sx={{ position:'absolute', inset:0, m:'auto', maxHeight:'100%', maxWidth:'100%', objectFit:'contain' }} />
          )}
          {promo && (
            <Box sx={{ position:'absolute', left:6, top:6, bgcolor:'error.main', color:'#fff',
                      px:1, py:0.25, borderRadius:1, fontSize:12, boxShadow:1 }}>
              Promo
            </Box>
          )}
          {qty>0 && !locked && (
            <Stack direction="row" spacing={1} alignItems="center"
                  sx={{ position:'absolute', right:6, bottom:6, bgcolor:'background.paper', px:0.5, py:0.25, borderRadius:999, boxShadow:1 }}>
              <IconButton color="error" size="small" onClick={()=>this.decProduct(p.id)}><Remove fontSize="small"/></IconButton>
              <Typography sx={{ minWidth:22, textAlign:'center', fontSize:13 }}>{qty}</Typography>
              <IconButton color="primary" size="small" onClick={()=>this.incProduct(p.id)}><Add fontSize="small"/></IconButton>
            </Stack>
          )}
        </Box>

        <CardContent sx={{ flexGrow:1, py:1, px:1.25 }}>
          <Typography variant="subtitle2" noWrap title={p.name}>{p.shortName || p.name}</Typography>
          {price!=null && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt:0.25 }}>
              <Typography variant="body2">{money(price, this.state.priceList?.currency||'RON')}</Typography>
              {promo && base!=null && (
                <Typography variant="caption" color="primary.main" sx={{ textDecoration:'line-through' }}>
                  {money(base, this.state.priceList?.currency||'RON')}
                </Typography>
              )}
            </Stack>
          )}
        </CardContent>

        <CardActions sx={{ p:1, pt:0 }}>
          <Button fullWidth variant="contained" size="small"
                  onClick={()=>this.addProduct(p)} startIcon={<Add />} sx={{ borderRadius:999 }} disabled={locked}>
            Add
          </Button>
        </CardActions>
      </Card>
    );
  }

  renderComboCard = (c)=> {
    const locked = this.state.orderLocked;
    const promo = !!c.isComboPromo;
    return (
      <Card variant="outlined"
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          transition: "transform 0.2s ease",
          "&:hover": { transform: "translateY(-3px)", boxShadow: "0 4px 12px rgba(0,0,0,0.12)" },
        }}
      >
        <Box sx={{ position:'relative', height:{ xs:100, sm:120 }, bgcolor:'background.default' }}>
          {c.imageSmallUrl && (
            <Box component="img" src={c.imageSmallUrl} alt={c.name}
                sx={{ position:'absolute', inset:0, m:'auto', maxHeight:'100%', maxWidth:'100%', objectFit:'contain' }} />
          )}
          <Box sx={{ position:'absolute', left:6, top:6, bgcolor:'secondary.main', color:'secondary.contrastText',
                    px:1, py:0.25, borderRadius:1, fontSize:12 }}>Combo</Box>
          {promo && (
            <Box sx={{ position:'absolute', right:6, top:6, bgcolor:'error.main', color:'#fff',
                      px:1, py:0.25, borderRadius:1, fontSize:12, boxShadow:1 }}>
              Promo
            </Box>
          )}
        </Box>
        <CardContent sx={{ flexGrow:1, py:1, px:1.25 }}>
          <Typography variant="subtitle2" noWrap title={c.name}>
            {c.shortName || c.name}
          </Typography>

          {c.basePriceC != null && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt:0.25 }}>
              {/* effective (promo-aware) price */}
              <Typography variant="body2">
                {money(fromCents(c.basePriceC), this.state.priceList?.currency || 'RON')}
              </Typography>

              {/* original/base price from PL, shown only when promo */}
              {c.isComboPromo && c.basePricePL != null && (
                <Typography
                  variant="caption"
                  color="primary.main"
                  sx={{ textDecoration: 'line-through' }}
                >
                  {money(c.basePricePL, this.state.priceList?.currency || 'RON')}
                </Typography>
              )}
            </Stack>
          )}
        </CardContent>
        <CardActions sx={{ p:1, pt:0 }}>
          <Button fullWidth variant="outlined" size="small"
                  onClick={()=>this.openComboPicker(c)} startIcon={<Tune />} sx={{ borderRadius:999 }} disabled={locked}>
            Select
          </Button>
        </CardActions>
      </Card>
    );
  };

  renderSideCartPanel() {
    const { priceList, cartProducts = {}, cartCombos = {}, orderLocked, placing } = this.state;
    const totals = priceList ? this.computeTotals() : { totalGross: 0, productGross: 0, discountGross: 0, discountNet: 0 };
    const currency = priceList?.currency || "RON";
    const hasLines =
      Object.keys(cartProducts).length > 0 || Object.keys(cartCombos).length > 0;
    const cartIsEmpty = !hasLines;

    const canUseSchedule = this.isScheduledOk();
    const checkoutDisabled =
      (!priceList) || (!hasLines) || placing || orderLocked ||
      (!this.state.locationOpenNow && !canUseSchedule);

    return (
      <Box
        sx={{
          position: "sticky",
          top: 80,
          width: { xs: "100%", lg: 400 },
          height: "calc(90vh - 80px)",
          display: "flex",
          flexDirection: "column",
          borderLeft: "1px solid #e0e0e0",
          bgcolor: "#fafafa",
        }}
      >
        {/* Header */}
        {/* <Box sx={{ p: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">Your order</Typography>
        </Box>
        <Divider /> */}

        {/* Cart list */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <List dense>
            {/* Individual products */}
            {Object.values(cartProducts).map((it) => {
              const unit = fromCents(it.unitPriceC);
              const lineTotal = unit * it.qty;
              return (
                <ListItem key={it.id} disableGutters sx={{ px: 1.25, py: 0.75 }}>
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%", minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                      <ListItemAvatar>
                        <Box sx={{ position: "relative", width: 56, height: 56 }}>
                          <Avatar
                            variant="rounded"
                            src={it.imageSmallUrl || undefined}
                            sx={{ width: 56, height: 56, borderRadius: 2 }}
                          >
                            {!it.imageSmallUrl && (it.name?.[0] || "?")}
                          </Avatar>
                          {it.qty > 1 && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: -6,
                                right: -6,
                                bgcolor: "primary.main",
                                color: "primary.contrastText",
                                fontSize: 16,
                                lineHeight: 1,
                                px: 0.6,
                                py: 0.4,
                                borderRadius: "999px",
                                boxShadow: 1,
                                minWidth: 18,
                                textAlign: "center",
                              }}
                            >
                              ×{it.qty}
                            </Box>
                          )}
                        </Box>
                      </ListItemAvatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{color: "secondary.main"}} variant="subtitle2" noWrap>
                          {it.name}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: "secondary.main", fontWeight: 700 }}>
                          {money(lineTotal, currency)}
                        </Typography>
                        {it.qty > 1 && (
                          <Typography variant="caption" color="primary.main">
                            {money(unit, currency)} / buc
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ ml: "auto", flexShrink: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <IconButton color="error" size="small" onClick={() => this.decProduct(it.id)}>
                          <Remove />
                        </IconButton>

                        <TextField
                          value={it.qty}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (!isNaN(v) && v > 0) this.updateProductQty(it.id, v);
                            else if (e.target.value === "") this.updateProductQty(it.id, ""); // allow typing
                          }}
                          onBlur={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (isNaN(v) || v <= 0) this.updateProductQty(it.id, 1);
                          }}
                          size="small"
                          variant="outlined"
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            min: 1,
                            style: { textAlign: "center", width: 40, padding: "4px 0" },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              height: 32,
                              borderRadius: 1,
                            },
                          }}
                        />

                        <IconButton color="primary" size="small" onClick={() => this.incProduct(it.id)}>
                          <Add />
                        </IconButton>
                      </Stack>
                    </Box>
                  </Box>
                </ListItem>
              );
            })}

            {/* Combo products */}
            {Object.entries(cartCombos).map(([key, it]) => {
              const qty = it.qty || 1;
              const all = [
                ...(it.selectionDetails || []),
                ...(it.fixedItems || []),
              ];
              const type1 = all.filter((x) => x.priceType === 1);
              const type2 = all.filter((x) => x.priceType === 2);
              const type0 = all.filter((x) => x.priceType === 0);

              const sum1UnitRON = type1.reduce(
                (acc, s) => acc + (this.state.effectiveProd?.[s.productId] || 0),
                0
              );
              const sum2UnitRON = type2.reduce(
                (acc, s) => acc + ((s.priceValueC || 0) / 100),
                0
              );
              const baseUnitRON = (it.basePriceC || 0) / 100;
              const lineTotal = Math.round(
                (baseUnitRON + sum1UnitRON + sum2UnitRON) * qty * 100
              ) / 100;

              return (
                <ListItem key={key} disableGutters sx={{ px: 1.25, py: 0.75 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", width: "100%", minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, minWidth: 0 }}>
                      <ListItemAvatar>
                        <Box sx={{ position: "relative", width: 56, height: 56 }}>
                          <Avatar
                            variant="rounded"
                            src={it.imageSmallUrl || undefined}
                            sx={{ width: 56, height: 56, borderRadius: 2 }}
                          >
                            {!it.imageSmallUrl && "🧩"}
                          </Avatar>
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: -4,
                              left: 0,
                              bgcolor: "secondary.main",
                              color: "secondary.contrastText",
                              fontSize: 10,
                              px: 0.5,
                              borderRadius: 1,
                              boxShadow: 1,
                            }}
                          >
                            Combo
                          </Box>
                        </Box>
                      </ListItemAvatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography  sx={{color: "secondary.main"}} variant="subtitle2" noWrap>
                          {it.name}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: "secondary.main", fontWeight: 700 }}>
                          {money(lineTotal, currency)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ ml: "auto", flexShrink: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <IconButton color="error" size="small" onClick={() => this.decCombo(key)}>
                          <Remove />
                        </IconButton>

                        <TextField
                          value={it.qty}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (!isNaN(v) && v > 0) this.updateComboQty(key, v);
                            else if (e.target.value === "") this.updateComboQty(key, ""); // allow empty while typing
                          }}
                          onBlur={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (isNaN(v) || v <= 0) this.updateComboQty(key, 1);
                          }}
                          size="small"
                          variant="outlined"
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            min: 1,
                            style: { textAlign: "center", width: 40, padding: "4px 0" },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              height: 32,
                              borderRadius: 1,
                            },
                          }}
                        />
                        <IconButton color="primary" size="small" onClick={() => this.incCombo(key)}>
                          <Add />
                        </IconButton>
                      </Stack>
                    </Box>
                  </Box>
                </ListItem>
              );
            })}

            {cartIsEmpty && (
              <ListItem>
                <ListItemText primary="Cart is empty." secondary="Add products." />
              </ListItem>
            )}
          </List>
        </Box>
        <Divider />

        {/* Footer */}
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            bgcolor: "#f0f0f0ff",
            borderTop: 1,
            borderColor: "divider",
            p: 1.5,
          }}
        >
          <Box sx={{ p: 1.5, pt: 0 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" sx={{ color: "secondary.main" }}>
                Discount
              </Typography>
              <Button
                variant={this.state.discountMode === "percent" ? "contained" : "outlined"}
                size="small"
                onClick={() => this.setState({ discountMode: "percent" })}
                sx={{ borderRadius: 999, minWidth: 36, px: 0 }}
              >
                %
              </Button>
              <Button
                variant={this.state.discountMode === "value" ? "contained" : "outlined"}
                size="small"
                onClick={() => this.setState({ discountMode: "value" })}
                sx={{ borderRadius: 999, minWidth: 48, px: 0 }}
              >
                RON
              </Button>
              <TextField
                size="small"
                value={this.state.discountInput}
                onChange={(e) => this.setState({ discountInput: e.target.value })}
                inputMode="decimal"
                variant="outlined"
                placeholder={this.state.discountMode === "percent" ? "10" : "5"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {this.state.discountMode === "percent"
                        ? "%"
                        : priceList?.currency || "RON"}
                    </InputAdornment>
                  ),
                }}
                sx={{
                  ml: 1,
                  maxWidth: 140,
                  flex: 1,
                  '& .MuiOutlinedInput-root': { borderRadius: 2, height: 36 },
                  '& .MuiInputBase-input': { textAlign: 'right' }, 
                }}
              />
            </Stack>

          </Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>Subtotal</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {money(totals.totalGrossBefore || totals.totalGross || 0, currency)}
            </Typography>
          </Box>

          {(totals.discountGross || 0) > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: "error.main" }}>Discount</Typography>
              <Typography variant="body2" sx={{ color: "error.main" }}>
                –{money(totals.discountGross, currency)}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 1 }} />

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6" sx={{ color: "secondary.main", fontWeight: 700 }}>
              Total
            </Typography>
            <Typography variant="h6" sx={{ color: "secondary.main", fontWeight: 700 }}>
              {money(totals.totalGross || 0, currency)}
            </Typography>
          </Box>
          
          <Divider />
          <Box sx={{ p: 1.5 }}>
            <TextField
              fullWidth
              select
              size="small"
              label="Payment type"
              value={this.state.paymentType}
              onChange={(e) => this.setState({ paymentType: Number(e.target.value) })}
              disabled={this.state.orderLocked}
              variant="outlined"
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      borderRadius: 2,
                      boxShadow: 3,
                    },
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            >
              <MenuItem value={2}>Bank Transfer</MenuItem>
              <MenuItem value={6}>Voucher</MenuItem>
            </TextField>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            {this.state.scheduledOn ? (
              <>
                <Typography sx={{ color: "secondary.main" }} variant="body2">
                  Scheduled at <b>{this.state.scheduledOn.format("ddd, DD MMM HH:mm")}</b>
                </Typography>
                <Button size="small" variant="text" onClick={this.clearSchedule}>
                  Remove
                </Button>
              </>
            ) : (
              // <Typography variant="body2" color="text.secondary">Scheduled now</Typography>
              null
            )}
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.5 }}>
            <Button variant="outlined" fullWidth onClick={this.clearCart} disabled={!hasLines}>
              Empty
            </Button>
            <Button
              variant="contained"
              endIcon={<Send />}
              fullWidth
              onClick={() => this.checkout()}
              disabled={checkoutDisabled}
            >
              {placing ? <CircularProgress size={18} sx={{ color: "white" }} /> : "Save order"}
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  }

  renderStart() {
    const tiles = (this.state.categoriesRaw || [])
      .filter(c => c.isForSale !== false)
      .map(c => ({
        id: c.iref || c.id,
        name: c.name,
        image: ensureSmallImage(c.image),
      }));

    const {
      location,                // selected location object
      locationList = [],         // array of { id, name, ... } or whatever you have
      selectingLocation,
      description = '',
      scheduledOn,             // dayjs() or null
      locationOpenNow,
      locationReason,
      currency,
      // other state you already use...
    } = this.state;

    const selectedLocation = locationList.find(l => (l.iref || l.id) === location) || null;

    const ok = (description || '').trim().length >= 2 && !!location;

    return (
      <Container
        maxWidth="xl"
        disableGutters
        sx={{
          mt: 1,
          bgcolor: "#fff",
          minHeight: "80vh",
          display: "flex",
          flexDirection: "column",
        }}
      >        

        {/* Gate form: location + description + scheduled date */}
        <Container maxWidth="lg" sx={{ px:2 }}>
          <Card variant="outlined" sx={{ p:2, mb:2 }}>
            <Grid container spacing={1.5}>
              {/* Location */}
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={locationList}
                  value={selectedLocation}
                  onChange={(e, val) => {
                    const id = val ? (val.iref || val.id) : null;
                    this.setState({ location: id }, () => this.bootstrap());
                  }}
                  getOptionLabel={(o) => o?.name ?? ''}
                  isOptionEqualToValue={(opt, val) => (opt.iref || opt.id) === (val?.iref || val?.id)}
                  loading={!!selectingLocation}
                  disabled={!!selectingLocation}
                  disableClearable
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Location"
                      placeholder="Select location"
                      fullWidth
                    />
                  )}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12} md={5}>
                <TextField
                  label="Description"
                  placeholder="Description for the order"
                  fullWidth
                  value={description}
                  onChange={(e) => this.setState({ description: e.target.value })}
                />
              </Grid>

              {/* Scheduled date/time (optional) */}
              <Grid item xs={12} md={3}>
                <Stack direction="column" spacing={1} alignItems="center" flexWrap="wrap">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => { 
                      e.currentTarget.blur();
                      this.setState({ cartOpen: false, showScheduleModal: true });
                    }}
                    sx={{ borderRadius: 2 }}
                  >
                    {this.state.scheduledOn
                      ? `Change time (${this.state.scheduledOn.format('DD MMM HH:mm')})`
                      : 'Choose time'}
                  </Button>

                  {this.state.scheduledOn ? (
                    <Chip
                      size="small"
                      label={`Scheduled at ${this.state.scheduledOn.format("ddd, DD MMM HH:mm")}`}
                      onDelete={this.clearSchedule}
                    />
                  ) : (
                    <Chip size="small" label="Scheduled now" color="default" />
                  )}
                </Stack>
              </Grid>

              {/* Start ordering / Continue */}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={!ok}
                  onClick={() => {
                    // If you still want to keep a "browse" view, set it here.
                    // Or stay on the same page since products are already below.
                    this.setState({ view: 'browse' });
                  }}
                >
                  Start ordering
                </Button>
                <Typography variant="caption" color="primary.main" sx={{ display:'block', mt:1 }}>
                  Add a short description and (optionally) schedule this order for later.
                </Typography>
              </Grid>
            </Grid>
          </Card>
        </Container>

        {/* Categories grid (old renderHome content) */}
        <Container maxWidth="xl" sx={{ px:2 }}>
          <Grid container spacing={1.25} sx={{ mt:1 }}>
            {tiles.map(t=> (
              <Grid key={t.id} item xs={6} sm={4} md={3} lg={2.4}>
                <Card onClick={()=>this.openCategory(t.name)}>
                  <Box sx={{ position:'relative', height:120, bgcolor:'background.default' }}>
                    {t.image && (
                      <Box
                        component="img"
                        src={t.image}
                        alt={t.name}
                        sx={{ position:'absolute', inset:0, m:'auto', maxHeight:'100%', maxWidth:'100%', objectFit:'contain' }}
                      />
                    )}
                  </Box>
                  <CardContent sx={{ py:1, px:1.25 }}>
                    <Typography variant="subtitle2" noWrap>{t.name}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="text"
              startIcon={<MenuBookOutlined />}
              onClick={() => this.setState({ view: 'browse', activeCategory: 'All' })}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.light',
                  color: 'white',
                },
              }}
            >
              See all Menu
            </Button>
          </Box>
        </Container>
      </Container>
    );
  }

  render(){

    const {
      loading, error, priceList, products, combos, categories, categoriesRaw, activeCategory, search,
      cartOpen, cartProducts = {}, cartCombos = {}, placing, view, orderLocked,
      paymentSheetOpen, paymentMessage, paymentChecking, payUrl, payIframeError,
      summaryOpen, lastOrderId, pickerOpen, pickerCombo
    } = this.state;

    const cartQty =
      Object.values(cartProducts).reduce((sum, l) => sum + (l?.qty || 0), 0) +
      Object.values(cartCombos).reduce((sum, l) => sum + (l?.qty || 0), 0);

    const cartIsEmpty = cartQty === 0;

    const currency = priceList?.currency || "RON";
    const s = (search || "").trim().toLowerCase();
    const catNameToId = new Map((categoriesRaw || []).map(c => [c.name, c.iref || c.id]));
    const currentCategoryId = catNameToId.get(activeCategory) || null;

    const filteredProducts = products
      .filter((p) => {
        if (activeCategory === "Combos") return false;
        const okCat =
          activeCategory === "All" ||
          p.categoryId === currentCategoryId ||
          p.categoryName === activeCategory;
        const okText =
          !s ||
          p.name?.toLowerCase().includes(s) ||
          p.categoryName?.toLowerCase().includes(s);
        return okCat && okText;
      })
      .sort((a, b) => a.shortName.localeCompare(b.shortName));

    const filteredCombos = combos
      .filter((c) => {
        const okCat =
          activeCategory === "All" ||
          activeCategory === "Combos" ||
          c.categoryId === currentCategoryId ||
          c.categoryName === activeCategory;

        const okText =
          !s ||
          c.name?.toLowerCase().includes(s) ||
          c.categoryName?.toLowerCase().includes(s);

        return okCat && okText;
      })
      .sort((a, b) => a.shortName.localeCompare(b.shortName));

    const itemsCount =
      Object.values(cartProducts).reduce((a, b) => a + (b.qty || 0), 0) +
      Object.values(cartCombos).reduce((a, b) => a + (b.qty || 0), 0);

    const totals = priceList ? this.computeTotals() : { totalGross: 0, productGross: 0, discountGross: 0, discountNet: 0 };
    const hasLines = Object.keys(cartProducts).length || Object.keys(cartCombos).length;
    const canUseSchedule = this.isScheduledOk();
    const checkoutDisabled =
      (!priceList) || (!hasLines) || placing || orderLocked ||
      (!this.state.locationOpenNow && !canUseSchedule);

    const inFlow = paymentSheetOpen || summaryOpen;

    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles styles={{
          'html, body': { touchAction: 'manipulation' },
          'button, [role="button"], .MuiButtonBase-root': { touchAction: 'manipulation' }
        }} />
        <Box 
          sx={{ 
            display: 'flex', flexDirection: 'column', height: '90vh',
            width: {
              xs: 'calc(100vw - 8px)',
              sm: '100%',
            },
            maxWidth: {
              xs: 'calc(100vw - 8px)',
              sm: '100%',
            },
            ml: {
              xs: 'calc(50% - 50vw + 4px)',
              sm: 0,
            }, 
          }}
          >
          <AppBar
            position="sticky"
            elevation={2}
            sx={{
              background: '#f0f0f0ff',
              color: 'text.primary',
              borderBottom: '1px solid #ddd',
            }}
          >
            <Toolbar
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                py: { xs: 1.5, sm: 1 },
                gap: { xs: 1, sm: 2 },
              }}
            >
              {/* Left side: Back button + title */}
              <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, width: '100%' }}>
                {view === 'browse' && !inFlow && (
                  <IconButton
                    edge="start"
                    color="inherit"
                    onClick={() => this.setState({ view: 'name' })}
                    sx={{ mr: 1 }}
                    aria-label="Back"
                  >
                    <ArrowBack />
                  </IconButton>
                )}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.1rem', sm: '1.5rem' },
                    mb: 0.25,
                  }}
                >
                  {this.state.locationInfo?.name || RESTAURANT_NAME}
                </Typography>
                <Typography
                  variant="h6"
                  noWrap
                  sx={{
                    fontWeight: 700,
                    flexGrow: 1,
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  }}
                >
                  {view === 'browse'
                    ? (activeCategory === 'All' ? 'Meniu' : activeCategory)
                    : ""}
                </Typography>
              </Box>

              {/* Center block: restaurant info + change schedule */}
              {view !== 'name' && !inFlow &&  (
                <Box
                  sx={{
                    textAlign: 'center',
                    flexGrow: { xs: 1, sm: 0 },
                    px: { xs: 0, sm: 2 },
                    minWidth: { sm: 320 },
                  }}
                >
                  {(() => {
                    const line = this.renderScheduleStatusLine();

                    return (
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                        flexWrap="wrap"
                      >
                        <Typography
                          variant="body2"
                          component="div"
                          sx={{
                            mt: 0.25,
                            fontWeight: 600,
                            color: this.state.locationOpenNow
                              ? 'success.main'
                              : 'error.main',
                          }}
                        >
                          {line}
                        </Typography>

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => { 
                            e.currentTarget.blur();
                            this.setState({ cartOpen: false, showScheduleModal: true });
                          }}
                          sx={{ borderRadius: 2 }}
                        >
                          {this.state.scheduledOn
                            ? `Change time (${this.state.scheduledOn.format('DD MMM HH:mm')})`
                            : 'Choose time'}
                        </Button>

                        {this.state.scheduledOn && (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => this.setState({ scheduledOn: null })}
                          >
                            Remove
                          </Button>
                        )}
                      </Stack>
                    );
                  })()}
                </Box>
              )}

              {/* Right side: cart + change location */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {!inFlow && (
                  <IconButton
                    color="inherit"
                    onClick={() => this.setState({ cartOpen: true })}
                    aria-label="Cart"
                    sx={{ display: { xs: 'inline-flex', md: 'inline-flex', lg: 'none' } }}
                  >
                    <Badge badgeContent={itemsCount} color="secondary">
                      <ShoppingCart />
                    </Badge>
                  </IconButton>
                )}

                {!inFlow && (
                  <Button
                    color="inherit"
                    variant="outlined"
                    sx={{ borderRadius: 2, display: { xs: 'none', md: 'inline-flex' }, textTransform: 'none' }}
                    onClick={() => this.changeLocation()}
                  >
                    Change location
                  </Button>
                )}
              </Box>
            </Toolbar>
          </AppBar>

          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {/* MAIN SWITCH */}
            {summaryOpen ? (
              <Box sx={{ height: '100%', overflow: 'auto' }}>
                <Container maxWidth="sm" sx={{ py: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    Thank you! Your order was saved.
                  </Typography>
                  <Typography variant="body2" color="primary.main" sx={{ mb: 2 }}>
                    Stay on this page for details.
                  </Typography>
                  <OrderSummary
                    id={lastOrderId}
                    currency={currency}
                    cartProducts={cartProducts}
                    cartCombos={cartCombos}
                    gross={this.state.lastOrderGross || (priceList ? totals.totalGross : 0)}
                    discountValue={
                      (this.state.orderResult?.discountValue) ??
                      (priceList ? totals.discountGross : 0)
                    }
                    locationName={this.state.locationInfo?.name}
                    description={this.state.description || ""}
                    onNewOrder={() => {
                      this.setState({
                        cartProducts: {}, cartCombos: {}, orderLocked: false, placing: false,
                        view: "name", activeCategory: "All", search: "", lastOrderGross: 0, discountInput: "", discountMode: "percent",
                        summaryOpen: false, lastOrderId: null, orderResult: null, lastOrderGross: 0,
                        description: "", scheduledOn: null, location: null, locationInfo: null, priceList: null, promoList: null
                      });
                    }}
                    {...(this.state.scheduledOn
                      ? { scheduleOn: this.state.scheduledOn.format("YYYY-MM-DD HH:mm:ssZ") }
                      : {})}
                  />
                </Container>
              </Box>
            ) : (
              view === 'browse' ? (
                <Box sx={{ height: '100%', display: 'flex', overflow: 'hidden' }}>
                  {/* LEFT pane: sticky search header + scrollable products */}
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      pr: { md: 1 },
                    }}
                  >
                    {/* Sticky header INSIDE the scroll container */}
                    <Box
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        bgcolor: '#f0f0f0ff',
                        py: 1,
                        px: { xs: 1, md: 2 },
                        boxShadow: 1,
                      }}
                    >
                      {/* Search */}
                      <TextField
                        value={search}
                        onChange={(e) => this.setState({ search: e.target.value })}
                        placeholder="Search products…"
                        size="small"
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search />
                            </InputAdornment>
                          ),
                          endAdornment: search ? (
                            <InputAdornment position="end">
                              <IconButton aria-label="Șterge filtrul" onClick={() => this.setState({ search: '' })}>
                                <Close />
                              </IconButton>
                            </InputAdornment>
                          ) : null,
                        }}
                      />

                      {/* Tabs */}
                      <Tabs
                        value={activeCategory}
                        onChange={(e, val) => this.setState({ activeCategory: val || 'All' })}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                          mt: 0.5,
                          '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            minHeight: 44,
                            mx: 0.25,
                            borderRadius: 2,
                            color: 'text.primary',
                          },
                          '& .MuiTab-root.Mui-selected': {
                            bgcolor: 'primary.main',
                            color: '#fff !important',
                          },
                          '& .MuiTabs-indicator': { display: 'none' },
                        }}
                      >
                        {categories.map((c) => (
                          <Tab key={c} value={c} label={c} />
                        ))}
                      </Tabs>

                      {!this.state.locationOpenNow && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="error">
                            {this.state.locationReason}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Scrollable products area fills remaining height */}
                    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                      <Container maxWidth="xl" sx={{ py: 1 }}>
                        {loading ? (
                          <Grid container spacing={1}>
                            {Array.from({ length: 10 }).map((_, i) => (
                              <Grid item xs={6} sm={4} md={3} lg={3} key={i}>
                                <Card variant="outlined">
                                  <CardContent sx={{ p: 1 }}>
                                    <Skeleton variant="rounded" height={100} />
                                    <Skeleton width="80%" sx={{ mt: 1 }} />
                                    <Skeleton width="40%" />
                                  </CardContent>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        ) : (
                          <Grid container spacing={1}>
                            {filteredProducts.map((p) => (
                              <Grid item xs={6} sm={4} md={3} lg={3} key={p.id}>
                                {this.renderProductCard(p)}
                              </Grid>
                            ))}
                            {filteredCombos.map((c) => (
                              <Grid item xs={6} sm={4} md={3} lg={3} key={c.id}>
                                {this.renderComboCard(c)}
                              </Grid>
                            ))}
                            {!filteredProducts.length && !filteredCombos.length && (
                              <Grid item xs={12}>
                                <Alert severity="info">Couldn't find products in this category.</Alert>
                              </Grid>
                            )}
                          </Grid>
                        )}
                      </Container>
                    </Box>
                  </Box>

                  {/* RIGHT pane: scrollable cart (desktop only) */}
                  <Box
                    sx={{
                      flexShrink: 0,                  // prevent shrinking
                      minWidth: 450,                  // ✅ enforce minimum 400px width
                      width: { lg: '32%' },           // still responsive
                      maxWidth: 520,
                      display: { xs: 'none', md: 'none', lg: 'flex' },
                      flexDirection: 'column',
                      borderLeft: 1,
                      borderColor: 'divider',
                      overflow: 'hidden',
                    }}
                  >
                    {/* make the side panel itself scroll inside 100vh */}
                    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
                      {this.renderSideCartPanel()}
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ height: '100%', overflow: 'auto' }}>
                  {this.renderStart()}
                </Box>
              )
            )}

            {/* Bottom mobile total bar */}
            {view !== 'name' && !summaryOpen && !inFlow && (
              <Box
                sx={{
                  position: 'fixed', bottom: 0, left: 0, right: 0,
                  bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider',
                  py: 1, boxShadow: '0 -6px 16px rgba(0,0,0,0.08)',
                  display: { xs: 'block', md: 'block', lg: 'none' }
                }}
              >
                <Container maxWidth="xl" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
                    Total: {money(totals.totalGross || 0, currency)}
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<ShoppingCart />}
                    onClick={() => !inFlow && this.setState({ cartOpen: true })}
                    sx={{ px: 3 }}
                  >
                    Cart ({itemsCount})
                  </Button>
                </Container>
              </Box>
            )}

            {/* Cart drawer */}
            {!inFlow && (
              <Drawer anchor="right" open={cartOpen && view !== 'name'} onClose={() => this.setState({ cartOpen: false })}>
                <Box sx={{ width: { xs: 360, sm: 420, md: 420 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  {/* <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Your order</Typography>
                  </Box>
                  <Divider /> */}
                  <Box sx={{ flex: 1, overflow: 'auto' }}>
                    <List dense>
                      {Object.values(cartProducts).map(it => {
                        const unit = fromCents(it.unitPriceC);
                        const lineTotal = unit * it.qty;
                        return (
                          <ListItem key={it.id} disableGutters sx={{ px: 1.25, py: 0.75 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                <ListItemAvatar>
                                  <Box sx={{ position: 'relative', width: 56, height: 56 }}>
                                    <Avatar variant="rounded" src={it.imageSmallUrl || undefined} sx={{ width: 56, height: 56, borderRadius: 2 }}>
                                      {(!it.imageSmallUrl && (it.name?.[0] || '?'))}
                                    </Avatar>
                                    {it.qty > 1 && (
                                      <Box sx={{ position: 'absolute', top: -6, right: -6, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: 16, lineHeight: 1, px: 0.6, py: 0.4, borderRadius: '999px', boxShadow: 1, minWidth: 18, textAlign: 'center' }}>
                                        ×{it.qty}
                                      </Box>
                                    )}
                                  </Box>
                                </ListItemAvatar>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="subtitle2" noWrap>{it.name}</Typography>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{money(lineTotal, currency)}</Typography>
                                  {it.qty > 1 && (<Typography variant="caption" color="primary.main">{money(unit, currency)} / buc</Typography>)}
                                </Box>
                              </Box>
                              <Box sx={{ ml: "auto", flexShrink: 0 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <IconButton color="error" size="small" onClick={() => this.decProduct(it.id)}>
                                    <Remove />
                                  </IconButton>

                                  <TextField
                                    value={it.qty}
                                    onChange={(e) => {
                                      const v = parseInt(e.target.value, 10);
                                      if (!isNaN(v) && v > 0) this.updateProductQty(it.id, v);
                                      else if (e.target.value === "") this.updateProductQty(it.id, ""); // allow typing
                                    }}
                                    onBlur={(e) => {
                                      const v = parseInt(e.target.value, 10);
                                      if (isNaN(v) || v <= 0) this.updateProductQty(it.id, 1);
                                    }}
                                    size="small"
                                    variant="outlined"
                                    inputProps={{
                                      inputMode: "numeric",
                                      pattern: "[0-9]*",
                                      min: 1,
                                      style: { textAlign: "center", width: 40, padding: "4px 0" },
                                    }}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        height: 32,
                                        borderRadius: 1,
                                      },
                                    }}
                                  />

                                  <IconButton color="primary" size="small" onClick={() => this.incProduct(it.id)}>
                                    <Add />
                                  </IconButton>
                                </Stack>
                              </Box>
                            </Box>
                          </ListItem>
                        );
                      })}

                      {Object.entries(cartCombos).map(([key, it]) => {
                        const qty = it.qty || 1;
                        const priceList = this.state.priceList;
                        const all = [
                          ...(it.selectionDetails || []),
                          ...(it.fixedItems || []),
                        ];
                        const type1 = all.filter(x => x.priceType === 1);
                        const type2 = all.filter(x => x.priceType === 2);
                        const type0 = all.filter(x => x.priceType === 0);

                        const sum1UnitRON = type1.reduce((acc, s) => acc + (this.state.effectiveProd?.[s.productId] || 0), 0);
                        const sum2UnitRON = type2.reduce((acc, s) => acc + ((s.priceValueC || 0) / 100), 0);
                        const baseUnitRON = (it.basePriceC || 0) / 100;

                        const P0 = type0.map(s => this.anchorForType0(s.productId));
                        const S0 = P0.reduce((a, b) => a + b, 0);
                        let alloc0UnitRON = [];
                        if (type0.length) {
                          if (S0 <= 0) {
                            const each = Math.round((baseUnitRON / type0.length) * 100) / 100;
                            alloc0UnitRON = Array(type0.length).fill(each);
                            const drift = Math.round((baseUnitRON - alloc0UnitRON.reduce((a, b) => Math.round((a + b) * 100) / 100, 0)) * 100) / 100;
                            if (Math.abs(drift) >= 0.01) alloc0UnitRON[0] = Math.round((alloc0UnitRON[0] + drift) * 100) / 100;
                          } else {
                            alloc0UnitRON = P0.map(p => Math.round((baseUnitRON * (p / S0)) * 100) / 100);
                            let sum = Math.round(alloc0UnitRON.reduce((a, b) => Math.round((a + b) * 100) / 100, 0) * 100) / 100;
                            let rem = Math.round((baseUnitRON - sum) * 100) / 100;
                            if (Math.abs(rem) >= 0.01) {
                              const idx = P0.indexOf(Math.max(...P0));
                              alloc0UnitRON[idx] = Math.round((alloc0UnitRON[idx] + rem) * 100) / 100;
                            }
                          }
                        }

                        const rows = [];
                        type1.forEach((s) => {
                          const vUnit = this.state.effectiveProd?.[s.productId] || 0;
                          rows.push({ name: s.name, value: Math.round(vUnit * qty * 100) / 100 });
                        });
                        type2.forEach((s) => {
                          const vUnit = (s.priceValueC || 0) / 100;
                          rows.push({ name: s.name, value: Math.round(vUnit * qty * 100) / 100 });
                        });
                        type0.forEach((s, i) => {
                          rows.push({ name: s.name, value: Math.round(alloc0UnitRON[i] * qty * 100) / 100 });
                        });

                        const unitLineRON = baseUnitRON + sum1UnitRON + sum2UnitRON;
                        const lineTotal = Math.round(unitLineRON * qty * 100) / 100;

                        const sumRows = Math.round(rows.reduce((a, b) => a + (b.value || 0), 0) * 100) / 100;
                        if (Math.abs(sumRows - lineTotal) >= 0.01) {
                          console.warn("[Cart combo] breakdown != line", { sumRows, lineTotal, rows, it });
                        }

                        return (
                          <ListItem key={key} disableGutters sx={{ px: 1.25, py: 0.75 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
                                <ListItemAvatar>
                                  <Box sx={{ position: 'relative', width: 56, height: 56 }}>
                                    <Avatar variant="rounded" src={it.imageSmallUrl || undefined} sx={{ width: 56, height: 56, borderRadius: 2 }}>
                                      {(!it.imageSmallUrl && '🧩')}
                                    </Avatar>
                                    <Box sx={{ position: 'absolute', bottom: -4, left: 0, bgcolor: 'secondary.main', color: 'secondary.contrastText', fontSize: 10, px: 0.5, borderRadius: 1, boxShadow: 1 }}>Combo</Box>
                                    {it.qty > 1 && (
                                      <Box sx={{ position: 'absolute', top: -6, right: -6, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: 16, lineHeight: 1, px: 0.6, py: 0.4, borderRadius: '999px', boxShadow: 1, minWidth: 18, textAlign: 'center' }}>
                                        ×{it.qty}
                                      </Box>
                                    )}
                                  </Box>
                                </ListItemAvatar>

                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography variant="subtitle2" noWrap>{it.name}</Typography>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{money(lineTotal, this.state.priceList?.currency || 'RON')}</Typography>

                                  <Box sx={{ mt: 0.25 }}>
                                    {rows.map((r, idx) => (
                                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="primary.main" noWrap sx={{ pr: 1 }}>{r.name}</Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 500 }}>{money(r.value, this.state.priceList?.currency || 'RON')}</Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              </Box>

                              <Box sx={{ ml: "auto", flexShrink: 0 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <IconButton color="error" size="small" onClick={() => this.decCombo(key)}>
                                    <Remove />
                                  </IconButton>

                                  <TextField
                                    value={it.qty}
                                    onChange={(e) => {
                                      const v = parseInt(e.target.value, 10);
                                      if (!isNaN(v) && v > 0) this.updateComboQty(key, v);
                                      else if (e.target.value === "") this.updateComboQty(key, ""); // allow empty while typing
                                    }}
                                    onBlur={(e) => {
                                      const v = parseInt(e.target.value, 10);
                                      if (isNaN(v) || v <= 0) this.updateComboQty(key, 1);
                                    }}
                                    size="small"
                                    variant="outlined"
                                    inputProps={{
                                      inputMode: "numeric",
                                      pattern: "[0-9]*",
                                      min: 1,
                                      style: { textAlign: "center", width: 40, padding: "4px 0" },
                                    }}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        height: 32,
                                        borderRadius: 1,
                                      },
                                    }}
                                  />

                                  <IconButton color="primary" size="small" onClick={() => this.incCombo(key)}>
                                    <Add />
                                  </IconButton>
                                </Stack>
                              </Box>

                            </Box>
                          </ListItem>
                        );
                      })}

                      {cartIsEmpty && (
                        <ListItem>
                          <ListItemText primary="Cart is empty." secondary="Add products." />
                        </ListItem>
                      )}
                    </List>
                  </Box>
                  <Divider />
                  <Box sx={{ position: 'sticky', bottom: 0, bgcolor: "#f0f0f0ff", borderTop: 1, borderColor: 'divider', p: 1.5 }}>
                    <Box sx={{ p: 1.5, pt: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 0.5 }}>
                        <Typography variant="subtitle1" sx={{ color: "secondary.main" }}>
                          Discount
                        </Typography>
                        <Button
                          variant={this.state.discountMode === "percent" ? "contained" : "outlined"}
                          size="small"
                          onClick={() => this.setState({ discountMode: "percent" })}
                          sx={{ borderRadius: 999, minWidth: 36, px: 0 }}
                        >
                          %
                        </Button>
                        <Button
                          variant={this.state.discountMode === "value" ? "contained" : "outlined"}
                          size="small"
                          onClick={() => this.setState({ discountMode: "value" })}
                          sx={{ borderRadius: 999, minWidth: 48, px: 0 }}
                        >
                          RON
                        </Button>
                        <TextField
                          size="small"
                          value={this.state.discountInput}
                          onChange={(e) => this.setState({ discountInput: e.target.value })}
                          inputMode="decimal"
                          variant="outlined"
                          placeholder={this.state.discountMode === "percent" ? "10" : "5"}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                {this.state.discountMode === "percent"
                                  ? "%"
                                  : priceList?.currency || "RON"}
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            ml: 1,
                            maxWidth: 140,
                            flex: 1,
                            '& .MuiOutlinedInput-root': { borderRadius: 2, height: 36 },
                            '& .MuiInputBase-input': { textAlign: 'right' }, 
                          }}
                        />
                      </Stack>

                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>Subtotal</Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {money(totals.totalGrossBefore || totals.totalGross || 0, currency)}
                      </Typography>
                    </Box>

                    {(totals.discountGross || 0) > 0 && (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: "error.main" }}>Discount</Typography>
                        <Typography variant="body2" sx={{ color: "error.main" }}>
                          –{money(totals.discountGross, currency)}
                        </Typography>
                      </Box>
                    )}

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography variant="h6" sx={{ color: "secondary.main", fontWeight: 700 }}>
                        Total
                      </Typography>
                      <Typography variant="h6" sx={{ color: "secondary.main", fontWeight: 700 }}>
                        {money(totals.totalGross || 0, currency)}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ p: 1.5 }}>
                      <TextField
                        fullWidth
                        select
                        size="small"
                        label="Payment type"
                        value={this.state.paymentType}
                        onChange={(e) => this.setState({ paymentType: Number(e.target.value) })}
                        disabled={this.state.orderLocked}
                        variant="outlined"
                        SelectProps={{
                          MenuProps: {
                            PaperProps: {
                              sx: {
                                borderRadius: 2,
                                boxShadow: 3,
                              },
                            },
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      >
                        <MenuItem value={2}>Bank Transfer</MenuItem>
                        <MenuItem value={6}>Voucher</MenuItem>
                      </TextField>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      {this.state.scheduledOn ? (
                        <>
                          <Typography variant="body2">
                            Scheduled at <b>{this.state.scheduledOn.format("ddd, DD MMM HH:mm")}</b>
                          </Typography>
                          <Button size="small" variant="text" onClick={this.clearSchedule}>
                            Remove
                          </Button>
                        </>
                      ) : (
                        // <Typography variant="body2" color="text.secondary">Ordering now</Typography>
                        null
                      )}
                    </Stack>
                    {!this.state.locationOpenNow && (
                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        {this.state.locationReason || "Location is not accepting orders now."}
                      </Typography>
                    )}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
                      <Button variant="outlined" fullWidth onClick={this.clearCart} disabled={!hasLines}>Empty</Button>
                      <Button variant="contained" endIcon={<Send />} fullWidth onClick={this.checkout} disabled={checkoutDisabled}>
                        {placing ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Save order'}
                      </Button>
                    </Stack>
                  </Box>
                </Box>
              </Drawer>
            )}

            <ComboPickerSheet
              open={pickerOpen}
              combo={pickerCombo}
              productsPool={this.state.productsAll || this.state.products}
              priceList={priceList}
              promoByProductId={this.state.promoByProductId}
              promoByComboId={this.state.promoByComboId}
              onClose={() => this.setState({ pickerOpen: false, pickerCombo: null })}
              onConfirm={this.confirmComboFromPicker}
            />
            <PaymentSheet
              open={paymentSheetOpen}
              payUrl={payUrl}
              message={paymentMessage}
              checking={paymentChecking}
              iframeError={payIframeError}
              onManualCheck={this.checkOrderStatus}
              onOpenNew={() => payUrl && window.open(payUrl, "_blank", "noopener")}
              onCancelOrder={this.cancelCurrentOrder}
            />

            <Snackbar open={!!error} autoHideDuration={6000} onClose={() => this.setState({ error: "" })}>
              <Alert onClose={() => this.setState({ error: "" })} severity="error" variant="filled">{error}</Alert>
            </Snackbar>
          </Box>

          <Dialog
            open={this.state.showScheduleModal}
            onClose={() => this.setState({ showScheduleModal: false })}
            keepMounted={false}
            fullWidth
            maxWidth="sm"
            aria-labelledby="schedule-title"
            TransitionProps={{ onEntered: (node) => node?.querySelector('[data-autofocus]')?.focus() }}
          >
            <DialogTitle data-autofocus id="schedule-title" tabIndex={-1} autoFocus>
              Choose pick-up time
            </DialogTitle>
            <DialogContent dividers>
              <Tabs
                value={this.state.scheduleTab}
                onChange={(_, v) => this.setState({ scheduleTab: v, scheduledOn: null })}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 2 }}
              >
                {Array.from({ length: 7 }, (_, i) => dayjs().startOf("day").add(i, "day"))
                  .map((d, i) => (
                    <Tab
                      key={d.format("YYYYMMDD")}
                      label={i === 0 ? "Today" : i === 1 ? "Tomorrow" : d.format("ddd DD")}
                    />
                ))}
              </Tabs>

              {/* Small chips for all intervals of the day; only valid ones are active */}
              {(() => {
                const days = Array.from({ length: 7 }, (_, i) => dayjs().startOf("day").add(i, "day"));
                const activeDay = days[this.state.scheduleTab];
                const chips = this.allDayChipsForDate(activeDay);
                const selected = this.state.scheduledOn;

                return (
                  <Grid container spacing={1}>
                    {chips.map(({ dt, label, available }) => {
                      const isSel = selected && dt.isSame(selected);
                      return (
                        <Grid item key={dt.valueOf()}>
                          <Chip
                            label={label}
                            size="small"
                            variant={isSel ? "filled" : "outlined"}
                            color={isSel ? "primary" : "default"}
                            clickable={available}
                            disabled={!available}
                            onClick={
                              available
                                ? () => this.setState({ scheduledOn: dt, showScheduleModal: false })
                                : undefined
                            }
                            sx={{ borderRadius: 1.5 }}
                          />
                        </Grid>
                      );
                    })}
                    {chips.length === 0 && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          No intervals for this day.
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                );
              })()}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => this.setState({ showScheduleModal: false })}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

        </Box>
      </ThemeProvider>
    );
  }
}